/**
 * 配置管理模块
 *
 * 本模块负责管理应用程序的所有全局配置项。
 * 主要功能：
 * - 管理纸张尺寸和 DPI 设置
 * - 管理显示缩放比例
 * - 管理图片压缩和处理参数（压缩质量、尺寸限制、格式转换等）
 * - 管理渲染质量控制参数（输出格式、抗锯齿、图像平滑、文本优化等）
 * - 管理对齐辅助线配置
 * - 提供单位转换和尺寸计算辅助方法
 *
 * 图片处理配置说明：
 * - enabled: 是否启用图片压缩处理
 * - quality: 压缩质量 (0-1)，影响文件大小和视觉质量
 * - maxDimension: 最大尺寸限制，防止内存溢出
 * - maxSizeMB: 最大文件大小限制
 * - supportedInputTypes: 支持的输入图片格式
 * - preferredOutputFormat: 首选输出格式
 * - allowFormatConversion: 是否允许自动格式转换
 *
 * 渲染质量配置说明：
 * - format: 输出格式 (png/jpg/webp)，PNG支持透明度，JPEG文件小，WebP压缩最好
 * - quality: 渲染质量 (0-1)，0.8-0.95为推荐范围
 * - multiplier: 缩放倍数，>1为高清输出
 * - enableAntiAliasing: 抗锯齿，提升边缘平滑度
 * - enableImageSmoothing: 图像平滑，减少像素化效果
 * - colorDepth: 色彩深度，auto为自动选择
 * - enableTextOptimization: 文本渲染优化，提升文字清晰度
 * - maxRenderSize: 最大渲染尺寸限制，防止内存溢出
 * - enableProgressiveRendering: 渐进式渲染，先低质量后高质量
 * - renderTimeout: 渲染超时时间，防止卡死
 * - enableHardwareAcceleration: 硬件加速，提升性能
 * - colorMode: 色彩模式控制，支持彩色(color)、灰度(grayscale)、黑白(blackwhite)输出
 */

import type { AppConfig, CanvasSize, AlignmentGuidelinesConfig, ImageProcessingConfig, RenderQualityConfig, TransmissionConfig } from './types';
import { UnitConverter } from './renderer';

/**
 * 配置管理器类
 * 
 * 集中管理应用的所有配置状态，提供统一的读写接口。
 */
class ConfigManager {
    /** 图片处理配置 */
    private imageProcessingConfig: ImageProcessingConfig = {
        enabled: true,                // 默认启用图片压缩
        quality: 0.85,                // 默认压缩质量
        maxDimension: 1000,           // 默认最大尺寸
        maxSizeMB: 1,                 // 默认最大文件大小
        supportedInputTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'],
        preferredOutputFormat: 'png', // 首选 PNG 格式
        allowFormatConversion: true   // 允许格式转换
    };

    /** 渲染质量配置 - 控制canvas渲染输出质量和性能 */
    private renderQualityConfig: RenderQualityConfig = {
        format: 'jpeg',                    // 输出格式(png/jpg)
        quality: 0.85,                     // 渲染质量(0-1)
        multiplier: 1,                     // 缩放倍数(1为原始尺寸，>1为高清输出，<1为降采样)
        enableAntiAliasing: true,          // 是否启用抗锯齿
        enableImageSmoothing: true,        // 是否启用图像平滑
        colorDepth: 'auto',                // 色彩深度('auto' | '8bit' | '24bit' | '32bit')
        colorMode: 'color',                // 色彩模式('color' | 'grayscale')
        enableTextOptimization: true,      // 是否启用文本渲染优化
        maxRenderSize: 4096,               // 最大渲染尺寸限制，防止内存溢出
        enableProgressiveRendering: false, // 是否启用渐进式渲染
        renderTimeout: 30000,              // 渲染超时时间，防止卡死
        enableHardwareAcceleration: true   // 是否启用硬件加速
    };

    /** 传输配置 - 控制数据传输和压缩行为 */
    private transmissionConfig: TransmissionConfig = {
        enableCompression: true,        // 默认启用数据压缩
        connectionTimeout: 10000,       // 连接超时10秒
        maxReconnectAttempts: 5,        // 最大重连次数
        reconnectInterval: 3000         // 重连间隔3秒
    };

    /** 应用核心配置 */
    private config: AppConfig = {
        width: 90,              // 默认宽度（mm）
        height: 110,            // 默认高度（mm）
        dpi: 300,               // 默认 DPI
        displayZoom: 1.0,       // 默认显示缩放
        imageMaxDimension: 1000,// 图片最大尺寸限制
        imageQuality: 0.85,     // 图片压缩质量
        imageMaxSizeMB: 1,      // 图片最大文件大小（MB）
        printTimeout: 3600000,  // 打印超时时间（ms）
        batchSize: 50,          // 批量处理大小
        imageProcessing: this.imageProcessingConfig,
        renderQuality: this.renderQualityConfig,
        transmission: this.transmissionConfig
    };

    /** 对齐辅助线配置 */
    private alignmentConfig: AlignmentGuidelinesConfig = {
        enabled: true,          // 启用辅助线
        snapThreshold: 5,       // 吸附阈值（像素）
        lineColor: '#FF00FF',   // 辅助线颜色
        lineWidth: 1            // 辅助线宽度
    };

    /**
     * 获取当前应用配置的副本
     */
    getConfig(): AppConfig {
        return { ...this.config };
    }

    /**
     * 更新应用配置
     * @param updates 部分配置更新
     */
    updateConfig(updates: Partial<AppConfig>): void {
        this.config = { ...this.config, ...updates };
    }

    /**
     * 设置纸张尺寸
     * @param width 宽度（mm）
     * @param height 高度（mm）
     */
    setPaperSize(width: number, height: number): void {
        this.config.width = width;
        this.config.height = height;
    }

    /**
     * 设置 DPI
     * @param dpi 每英寸点数
     */
    setDPI(dpi: number): void {
        this.config.dpi = dpi;
    }

    /**
     * 设置显示缩放比例
     * @param zoom 缩放比例（0.2 - 2.0）
     */
    setDisplayZoom(zoom: number): void {
        this.config.displayZoom = Math.max(0.2, Math.min(2.0, zoom));
    }

    /**
     * 获取当前显示缩放比例
     */
    getDisplayZoom(): number {
        return this.config.displayZoom;
    }

    /**
     * 计算画布像素尺寸
     * 根据当前的物理尺寸（mm）和 DPI 计算像素尺寸
     */
    calculateCanvasSize(): CanvasSize {
        return UnitConverter.calculateCanvasSize(this.config.width, this.config.height, this.config.dpi);
    }

    /**
     * 计算推荐的显示缩放比例
     * 根据 DPI 自动调整显示缩放，以便在屏幕上获得合适的显示大小
     */
    calculateDisplayZoom(): number {
        if (this.config.dpi === 600) return 0.3;
        if (this.config.dpi === 300) return 0.6;
        if (this.config.dpi === 150) return 1.0;
        return 0.6;
    }

    /**
     * 获取 DPI 缩放因子
     * 相对于标准屏幕 DPI (72) 的比例
     */
    getDPIScale(): number {
        return this.config.dpi / 72;
    }

    /**
     * 获取图片压缩配置（兼容旧版本）
     */
    getImageCompressConfig() {
        return {
            maxDimension: this.config.imageMaxDimension,
            quality: this.config.imageQuality,
            maxSizeMB: this.config.imageMaxSizeMB
        };
    }

    /**
     * 获取完整的图片处理配置
     */
    getFullImageProcessingConfig(): ImageProcessingConfig {
        return this.getImageProcessingConfig();
    }

    /**
     * 获取渲染质量配置
     */
    getRenderConfig(): RenderQualityConfig {
        return this.getRenderQualityConfig();
    }

    /**
     * 获取支持的图片 MIME 类型列表
     */
    getSupportedImageTypes(): string[] {
        return this.imageProcessingConfig.supportedInputTypes;
    }

    /**
     * 获取文件选择器的 accept 属性字符串
     */
    getImageAcceptString(): string {
        return this.getSupportedImageTypes().join(',');
    }

    /**
     * 获取对齐辅助线配置
     */
    getAlignmentConfig(): AlignmentGuidelinesConfig {
        return { ...this.alignmentConfig };
    }

    /**
     * 更新对齐辅助线配置
     * @param updates 部分配置更新
     */
    updateAlignmentConfig(updates: Partial<AlignmentGuidelinesConfig>): void {
        this.alignmentConfig = { ...this.alignmentConfig, ...updates };
    }

    /**
     * 获取图片处理配置
     */
    getImageProcessingConfig(): ImageProcessingConfig {
        return { ...this.imageProcessingConfig };
    }

    /**
     * 更新图片处理配置
     * @param updates 部分配置更新
     */
    updateImageProcessingConfig(updates: Partial<ImageProcessingConfig>): void {
        this.imageProcessingConfig = { ...this.imageProcessingConfig, ...updates };
        // 同步更新主配置中的图片处理配置
        this.config.imageProcessing = { ...this.imageProcessingConfig };
    }

    /**
     * 获取渲染质量配置
     */
    getRenderQualityConfig(): RenderQualityConfig {
        return { ...this.renderQualityConfig };
    }

    /**
     * 获取传输配置
     */
    getTransmissionConfig(): TransmissionConfig {
        return { ...this.transmissionConfig };
    }

    /**
     * 更新渲染质量配置
     * @param updates 部分配置更新
     */
    updateRenderQualityConfig(updates: Partial<RenderQualityConfig>): void {
        this.renderQualityConfig = { ...this.renderQualityConfig, ...updates };
        // 同步更新主配置中的渲染质量配置
        this.config.renderQuality = { ...this.renderQualityConfig };
    }

    /**
     * 更新传输配置
     * @param updates 部分配置更新
     */
    updateTransmissionConfig(updates: Partial<TransmissionConfig>): void {
        this.transmissionConfig = { ...this.transmissionConfig, ...updates };
        // 同步更新主配置中的传输配置
        this.config.transmission = { ...this.transmissionConfig };
    }

    /**
     * 设置是否启用数据压缩
     * @param enabled 是否启用压缩
     */
    setCompressionEnabled(enabled: boolean): void {
        this.updateTransmissionConfig({ enableCompression: enabled });
    }


    /**
     * 设置是否启用图片压缩
     * @param enabled 是否启用
     */
    setImageCompressionEnabled(enabled: boolean): void {
        this.updateImageProcessingConfig({ enabled });
    }

    /**
     * 设置图片压缩质量
     * @param quality 压缩质量 (0-1)
     */
    setImageCompressionQuality(quality: number): void {
        this.updateImageProcessingConfig({ quality: Math.max(0, Math.min(1, quality)) });
    }

    /**
     * 设置渲染输出格式
     * @param format 输出格式
     */
    setRenderOutputFormat(format: 'png' | 'jpeg' | 'webp'): void {
        this.updateRenderQualityConfig({ format });
    }

    /**
     * 设置渲染质量
     * @param quality 渲染质量 (0-1)
     */
    setRenderQuality(quality: number): void {
        this.updateRenderQualityConfig({ quality: Math.max(0, Math.min(1, quality)) });
    }

    /**
     * 设置渲染缩放倍数
     * @param multiplier 缩放倍数
     */
    setRenderMultiplier(multiplier: number): void {
        this.updateRenderQualityConfig({ multiplier: Math.max(0.1, Math.min(5, multiplier)) });
    }

    /**
     * 设置是否启用文本渲染优化
     * @param enabled 是否启用
     */
    setTextOptimizationEnabled(enabled: boolean): void {
        this.updateRenderQualityConfig({ enableTextOptimization: enabled });
    }

    /**
     * 设置最大渲染尺寸
     * @param maxSize 最大尺寸（像素）
     */
    setMaxRenderSize(maxSize: number): void {
        this.updateRenderQualityConfig({ maxRenderSize: Math.max(512, Math.min(8192, maxSize)) });
    }

    /**
     * 设置是否启用渐进式渲染
     * @param enabled 是否启用
     */
    setProgressiveRenderingEnabled(enabled: boolean): void {
        this.updateRenderQualityConfig({ enableProgressiveRendering: enabled });
    }

    /**
     * 设置渲染超时时间
     * @param timeout 超时时间（毫秒）
     */
    setRenderTimeout(timeout: number): void {
        this.updateRenderQualityConfig({ renderTimeout: Math.max(5000, Math.min(120000, timeout)) });
    }

    /**
     * 设置是否启用硬件加速
     * @param enabled 是否启用
     */
    setHardwareAccelerationEnabled(enabled: boolean): void {
        this.updateRenderQualityConfig({ enableHardwareAcceleration: enabled });
    }

    /**
     * 设置色彩模式
     * @param mode 色彩模式 ('color' | 'grayscale' | 'blackwhite')
     */
    setColorMode(mode: 'color' | 'grayscale' | 'blackwhite'): void {
        this.updateRenderQualityConfig({ colorMode: mode });
    }
}

// 导出单例实例
export const configManager = new ConfigManager();

/**
 * 配置使用示例：
 *
 * // 设置高质量渲染配置
 * configManager.setRenderOutputFormat('png');
 * configManager.setRenderQuality(0.95);
 * configManager.setRenderMultiplier(2); // 高清输出
 *
 * // 设置色彩模式
 * configManager.setColorMode('grayscale'); // 灰度输出
 * configManager.setColorMode('color'); // 彩色输出（默认）
 *
 * // 启用所有优化功能
 * configManager.setTextOptimizationEnabled(true);
 * configManager.setHardwareAccelerationEnabled(true);
 *
 * // 设置渲染限制
 * configManager.setMaxRenderSize(4096);
 * configManager.setRenderTimeout(30000);
 *
 * // 图片处理配置
 * configManager.setImageCompressionEnabled(true);
 * configManager.setImageCompressionQuality(0.85);
 */
